import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	clearMonarchAccountRule,
	createMonarchAccount,
	saveMonarchAccountRule,
} from '../../../redux/actions/import.js';
import { moneyFormatFromCents } from '../../../utilities/moneyFormat';
import Button from '../../components/Button.jsx';
import { AccountMapRow } from '../settings/ImportAccountMapper.jsx';

/**
 * Which of Monarch's accounts to show: by default those still needing a decision -- open and
 * unmapped -- and every one when asked.
 */
export function monarchAccountsToShow(accounts, showAll) {
	if(showAll) {
		return accounts;
	}
	return accounts.filter((account) => !account.map && !account.closed);
}

/** The accounts here that some Monarch account already maps to, so none is suggested twice. */
export function mappedAccountIds(accounts) {
	return new Set(accounts
		.filter((account) => account.map && account.map.action === 'assign' && account.map.plaid_account_id)
		.map((account) => Number(account.map.plaid_account_id)));
}

/**
 * Monarch's accounts as the last "Refresh balances" listed them, mapped from here without a batch.
 *
 * An account with no transactions is in no export and no fetch, so the import page's mapper never
 * shows it; its balance is all there is to bring in. Maps saved here are the same budget-wide rules an
 * import uses, saved by the account's name. A map saved within a few minutes of the refresh writes
 * the account's balance from what that refresh read; after that, the next refresh does. The table is
 * the import page's own row, so picking, skipping and creating an account work as there.
 */
export default function MonarchAccountMapper() {
	const dispatch = useDispatch();
	const importing = useSelector((state) => state.importing || {});
	const [showAll, setShowAll] = useState(false);
	const [collapsed, setCollapsed] = useState(false);
	/** Why a row's last save was refused, by name: the row's to show, not the page's. */
	const [rowErrors, setRowErrors] = useState({});
	const accounts = importing.monarchAccounts;
	const taken = useMemo(() => mappedAccountIds(accounts || []), [accounts]);

	if(!accounts) {
		return null;
	}

	const mappedCount = accounts.filter((account) => !!account.map).length;
	const shown = monarchAccountsToShow(accounts, showAll);

	const settle = (key) => (result) => {
		setRowErrors((errors) => ({ ...errors, [key]: (result && result.error) || null }));
	};

	return (
		<div className='import-settings monarch_accounts_panel'>
			<div className='monarch_accounts_heading'>
				<h6>
					Monarch accounts{' '}
					<span className='grey-text'>({mappedCount} of {accounts.length} mapped)</span>
				</h6>
				<div>
					{!collapsed &&
					<Button small flat onClick={() => setShowAll((current) => !current)}>
						{showAll ? 'Show only unmapped' : `Show all ${accounts.length}`}
					</Button>
					}
					<Button small flat className='monarch_accounts_toggle' onClick={() => setCollapsed((current) => !current)}>
						{collapsed ? 'Show' : 'Hide'}
					</Button>
				</div>
			</div>
			{!collapsed &&
			<React.Fragment>
				<p className='grey-text'>
					Point each Monarch account at an account here, create one for it, or skip it. Its balance is
					recorded straight away if the last refresh was in the past few minutes, and otherwise by the
					next refresh. Imports use the same mappings.
				</p>
				{shown.length === 0 &&
				<p className='grey-text'>Every open Monarch account is mapped or skipped.</p>
				}
				{shown.length > 0 &&
				<div className='import-table-scroll'>
					<table className='import-table import-map-table'>
						<thead>
							<tr>
								<th>In Monarch</th>
								<th className='right-align'>Balance</th>
								<th>Maps to</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{shown.map((account, index) => {
								const key = account.external_account;
								return (
									<AccountMapRow
										key={`${key}#${index}`}
										account={account}
										availableAccounts={importing.availableAccounts}
										accountTypes={importing.accountTypes}
										takenAccountIds={taken}
										error={rowErrors[key]}
										notice={account.shared_name
											? 'Another open Monarch account has this name, so their balances cannot be told apart and neither is recorded.'
											: null}
										onSave={(plaidAccountId, action) =>
											dispatch(saveMonarchAccountRule(key, plaidAccountId, action)).then(settle(key))}
										onClear={() => dispatch(clearMonarchAccountRule(key)).then(settle(key))}
										onCreate={(newAccount) =>
											dispatch(createMonarchAccount(key, newAccount)).then(settle(key))}
									>
										<td className='right-align grey-text import-map-detail import-nowrap'>
											{account.balance === null ? '' : moneyFormatFromCents(account.balance)}
											{account.closed && <div className='import-suggestion'>closed in Monarch</div>}
										</td>
									</AccountMapRow>
								);
							})}
						</tbody>
					</table>
				</div>
				}
			</React.Fragment>
			}
		</div>
	);
}
